import React, { useEffect, useState } from 'react';
import ArraysUtils from 'utils/arrays';
import { connect, useSelector } from 'react-redux';
import * as pageHeaderSelectors from 'containers/PageHeader/selectors';
import {
  clientIdSelector,
  currentBrandSelector,
  enterpriseBrandSelector,
} from 'store/global/selectors';

import { ModalOptionsItem, ModalOptions } from '@spins/react-ui-library';
import { LIST_ACCOUNTS, LIST_PRODUCTS, LIST_STATES, LIST_STORES } from 'apollo/queries';
import {
  ItemType,
  MENU_SECTIONS,
  ProductOptionContentParams,
  ProductSelection,
  ProductType,
  QueryFiltersType,
} from 'components/ProductsSelection/types';
import ProductOptions from 'components/ProductsSelection/ProductOptions';

import useScrollableQuery from 'hooks/use-scrollable-query';
import * as actions from './store/actions';
import Selections from 'components/ProductsSelection/Selections';
import MenuTrailingBadge from 'components/ProductsSelection/MenuTrailingBadge';
import { getQueryOptions } from './utils';
import { createLabelsForFilters, dataMappers } from 'components/ProductsSelection/utils';
import * as selectors from './store/selectors';
import styles from 'components/ProductsSelection/styles.module.scss';

import { StoreDataType, ProductDataType } from 'utils/types';
import { TypeOfFilter } from './types';
import { productsClient } from 'apollo';

interface Props {
  id?: string;
  selection: ProductSelection;
  typeOfFilter: TypeOfFilter;
  storesFromEntity: Array<StoreDataType>;
  productsFromEntity: Array<ProductDataType>;
  clearSelections: () => void;
  onApplyValues: (currentProductType: ProductType, value: Array<ItemType>) => void;
}

const FilterModal: React.FC<Props> = ({
  typeOfFilter,
  selection,
  storesFromEntity,
  productsFromEntity,
  clearSelections,
  onApplyValues,
}) => {
  const clientId = useSelector(clientIdSelector);
  const currentBrand = useSelector(currentBrandSelector);
  const enterpriseBrand = useSelector(enterpriseBrandSelector);
  const currentWeeks = useSelector<number>(pageHeaderSelectors.globalFilterWeeksSelector);

  let appliedSelection: ProductSelection = {
    accounts: [],
    states: [],
    stores: [],
    items: [],
  };

  selection &&
    MENU_SECTIONS.forEach((section: ProductType) => {
      // eslint-disable-next-line array-callback-return
      selection[section].map((item) => {
        appliedSelection[section].push(item);
      });
    });

  // This should be done because there are some inconsistent data in the backend
  appliedSelection = createLabelsForFilters('stores', storesFromEntity, appliedSelection);
  appliedSelection = createLabelsForFilters('items', productsFromEntity, appliedSelection);

  const [transientSelection, setTransientSelection] = useState<ProductSelection>(appliedSelection);

  const hasNewFilters =
    (transientSelection.accounts.length === 0 && appliedSelection.accounts.length > 0) ||
    (transientSelection.states.length === 0 && appliedSelection.states.length > 0) ||
    (transientSelection.stores.length === 0 && appliedSelection.stores.length > 0) ||
    (transientSelection.items.length === 0 && appliedSelection.items.length > 0);

  useEffect(() => {
    if (hasNewFilters) {
      setTransientSelection((prevState) => {
        return { ...prevState, ...appliedSelection };
      });
    }
  }, [hasNewFilters, appliedSelection]);

  const commonQueryFilters: QueryFiltersType = {
    ...appliedSelection,
    weeks: currentWeeks,
    brand: enterpriseBrand ? currentBrand : '',
    clientID: clientId,
  };

  const skipStores = typeOfFilter === 'items';

  const {
    error: accountsError,
    loading: accountsLoading,
    data: accountsData,
    searching: accountsSearching,
    pagination: accountsPagination,
  } = useScrollableQuery(
    LIST_ACCOUNTS,
    getQueryOptions(
      {
        productType: 'accounts',
        defaultSortAttribute: 'name',
        defaultSearchAttribute: ['name'],
        resultAttribute: 'accounts',
      },
      commonQueryFilters,
      selection,
      productsClient,
      skipStores
    )
  );

  const {
    error: statesError,
    loading: statesLoading,
    data: statesData,
    searching: statesSearching,
    pagination: statesPagination,
  } = useScrollableQuery(
    LIST_STATES,
    getQueryOptions(
      {
        productType: 'states',
        defaultSortAttribute: 'code',
        defaultSearchAttribute: ['code'],
        resultAttribute: 'states',
      },
      commonQueryFilters,
      selection,
      productsClient,
      skipStores
    )
  );

  const {
    error: storesError,
    loading: storesLoading,
    data: storesData,
    searching: storesSearching,
    pagination: storesPagination,
  } = useScrollableQuery(
    LIST_STORES,
    getQueryOptions(
      {
        productType: 'stores',
        defaultSortAttribute: 'name',
        defaultSearchAttribute: ['name'],
        resultAttribute: 'stores',
      },
      commonQueryFilters,
      selection,
      productsClient,
      skipStores
    )
  );

  const skipItems = typeOfFilter === 'stores';

  const options = getQueryOptions(
    {
      productType: 'items',
      defaultSortAttribute: 'name',
      defaultSearchAttribute: ['name'],
      resultAttribute: 'products',
    },
    commonQueryFilters,
    selection,
    productsClient,
    skipItems
  );

  const {
    error: itemsError,
    loading: itemsLoading,
    data: itemsData,
    searching: itemsSearching,
    pagination: itemsPagination,
  } = useScrollableQuery(LIST_PRODUCTS, options);

  const accounts = accountsData ? accountsData.accounts.data : [];
  const totalAccounts = accountsData ? accountsData.accounts.page.totalItems : 0;
  const states = statesData ? statesData.states.data : [];
  const totalStates = statesData ? statesData.states.page.totalItems : 0;
  const stores = storesData ? storesData.stores.data : [];
  const totalStores = storesData ? storesData.stores.page.totalItems : 0;
  const items = itemsData ? itemsData.products.data : [];
  const totalItems = itemsData ? itemsData.products.page.totalItems : 0;

  const isApplyDisabled = (productType: ProductType) => {
    const transientIds = transientSelection[productType].flatMap((item) => item.id);
    const appliedIds = appliedSelection[productType].flatMap((item) => item.id);
    return ArraysUtils.equals(transientIds, appliedIds);
  };

  const setSelection = (productType: ProductType, selection: Array<ItemType>) => {
    onApplyValues(productType, selection);
  };

  const handleTransientChange = (currentProductType: ProductType, value: ItemType) => {
    setTransientSelection((prevState) => {
      const newValues = ArraysUtils.addOrRemoveObjectByKey(
        value,
        prevState[currentProductType],
        'id'
      );

      return {
        ...prevState,
        [currentProductType]: [...newValues],
      };
    });
  };

  const handleTransientSet = (currentProductType: ProductType, value: Array<ItemType>) => {
    setTransientSelection((prevState) => {
      return {
        ...prevState,
        [currentProductType]: value,
      };
    });
  };

  const handleClearSelections = () => {
    setTransientSelection({
      accounts: [],
      stores: [],
      states: [],
      items: [],
    });
    clearSelections();
  };

  const handleRemoveAll = (currentProductType: ProductType) => {
    setSelection(currentProductType, []);
    handleTransientSet(currentProductType, []);
  };

  const handleRemoveItem = (currentProductType: ProductType, item: ItemType) => {
    const newAppliedSelection = ArraysUtils.addOrRemove(item, appliedSelection[currentProductType]);
    setSelection(currentProductType, newAppliedSelection);
    const newTransient = ArraysUtils.addOrRemoveObjectByKey(
      item,
      transientSelection[currentProductType],
      'id'
    );
    handleTransientSet(currentProductType, newTransient);
  };

  const renderOptionContent = (options: ProductOptionContentParams) => {
    return (
      <ProductOptions
        allAvailable={true}
        queryResult={options.queryResult}
        totalItems={options.totalItems}
        transientSelection={transientSelection}
        appliedSelection={appliedSelection}
        currentProductType={options.productType}
        dataItemMapper={options.dataItemMapper}
        onTransientChange={handleTransientChange}
        onTransientSet={handleTransientSet}
        scrollableTarget="infinite-scroll-content-id"
      />
    );
  };

  const storesOptions: Array<ModalOptionsItem> = [
    {
      applyDisabled: isApplyDisabled('accounts'),
      content: renderOptionContent({
        queryResult: {
          error: accountsError,
          loading: accountsLoading,
          data: accounts,
          searching: accountsSearching,
          pagination: accountsPagination,
        },
        productType: 'accounts',
        totalItems: totalAccounts,
        ...dataMappers['accounts'],
      }),
      id: '1',
      menuTitle: 'Accounts',
      title: 'Accounts ',
      onApply: () => {
        setSelection('accounts', transientSelection['accounts']);
      },
      menuRightElement: () => {
        return (
          <MenuTrailingBadge
            loading={accountsLoading}
            totalCount={totalAccounts}
            selectedCount={selection['accounts'].length}
          />
        );
      },
    },
    {
      applyDisabled: isApplyDisabled('states'),
      content: renderOptionContent({
        queryResult: {
          error: statesError,
          loading: statesLoading,
          data: states,
          searching: statesSearching,
          pagination: statesPagination,
        },
        productType: 'states',
        totalItems: totalStates,
        ...dataMappers['states'],
      }),
      id: '2',
      menuTitle: 'States',
      title: 'States ',
      onApply: () => {
        setSelection('states', transientSelection['states']);
      },
      menuRightElement: () => {
        return (
          <MenuTrailingBadge
            loading={statesLoading}
            totalCount={totalStates}
            selectedCount={selection['states'].length}
          />
        );
      },
    },
    {
      applyDisabled: isApplyDisabled('stores'),
      content: renderOptionContent({
        queryResult: {
          error: storesError,
          loading: storesLoading,
          data: stores,
          searching: storesSearching,
          pagination: storesPagination,
        },
        productType: 'stores',
        totalItems: totalStores,
        ...dataMappers['stores'],
      }),
      id: '3',
      menuTitle: 'Stores',
      title: 'Stores ',
      onApply: () => {
        setSelection('stores', transientSelection['stores']);
      },
      menuRightElement: () => {
        return (
          <MenuTrailingBadge
            loading={storesLoading}
            totalCount={totalStores}
            selectedCount={selection['stores'].length}
          />
        );
      },
    },
  ];

  const itemsOptions = [
    {
      applyDisabled: isApplyDisabled('items'),
      content: renderOptionContent({
        queryResult: {
          error: itemsError,
          loading: itemsLoading,
          data: items,
          searching: itemsSearching,
          pagination: itemsPagination,
        },
        productType: 'items',
        totalItems: totalItems,
        ...dataMappers['items'],
      }),
      id: typeOfFilter === 'items' ? '1' : '4',
      menuTitle: 'Items',
      title: 'Items',
      onApply: () => setSelection('items', transientSelection['items']),
      menuRightElement: () => {
        return (
          <MenuTrailingBadge
            loading={itemsLoading}
            totalCount={totalItems}
            selectedCount={selection['items'].length}
          />
        );
      },
    },
  ];

  let modalOptions = [...storesOptions, ...itemsOptions];

  switch (typeOfFilter) {
    case 'stores':
      modalOptions = [...storesOptions];
      break;
    case 'items':
      modalOptions = [...itemsOptions];
      break;
    default:
      break;
  }

  return (
    <div className={styles['modal-body']}>
      <div className={styles['modal-options-container']}>
        <ModalOptions
          key={typeOfFilter}
          className={styles['modal-options']}
          options={modalOptions}
          selectedOption="1"
          onClearAll={handleClearSelections}
        />
      </div>
      <div className={styles['selection-section']}>
        <Selections
          appliedSelection={appliedSelection}
          clientId={clientId}
          currentBrand={enterpriseBrand ? currentBrand : ''}
          onRemoveAll={handleRemoveAll}
          onRemoveItem={handleRemoveItem}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  selection: selectors.selectionSelector(state),
});

const mapDispatchToProps = (dispatch) => ({
  onSetTransientValues: (currentProductType: ProductType, value: Array<ItemType>) => {
    dispatch(actions.setTransientValues(currentProductType, value));
  },
  onApplyValues: (currentProductType: ProductType, value: Array<ItemType>) => {
    dispatch(actions.applySelectionWithValues(currentProductType, value));
  },
  clearSelections: () => {
    dispatch(actions.clearSelections());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(FilterModal);
